import { useMemo, MouseEvent as ReactMouseEvent } from "react"

export type PaginationAction = { type: "next" } | { type: "prev" } | { type: "goto"; index: number }

interface Props {
    currentPage: number
    prevPage?: number
    nextPage?: number
    pagesCount: number
    getPageUrl?: (arg: number) => string
    actionHandler?: (action: PaginationAction) => void
}

export function PaginationView({
    currentPage,
    prevPage,
    nextPage,
    pagesCount,
    getPageUrl,
    actionHandler,
}: Props) {
    const prevPageUrl = prevPage ? getPageUrl?.(prevPage) : "#prev"
    const nextPageUrl = nextPage ? getPageUrl?.(nextPage) : "#next"
    const windowStart = Math.max(0, currentPage - 3)
    const windowEnd = Math.min(pagesCount - 1, windowStart + 4)
    const handlers = useMemo(
        () => ({
            triggerPrev(this: void, e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) {
                if (!actionHandler) return
                e.preventDefault()
                actionHandler({ type: "prev" })
            },
            triggerNext(this: void, e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) {
                if (!actionHandler) return
                e.preventDefault()
                actionHandler({ type: "next" })
            },
            triggerGoto(
                this: void,
                e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>,
                index: number
            ) {
                if (!actionHandler) return
                e.preventDefault()
                actionHandler({ type: "goto", index })
            },
        }),
        [currentPage]
    )
    return (
        <div style={{ display: "flex", justifyContent: "center" }}>
            <ul className="pagination">
                <li className={"page-item" + (currentPage === 1 ? " disabled" : "")}>
                    <a className="page-link" href={prevPageUrl} onClick={handlers.triggerPrev}>
                        <span aria-hidden="true">&laquo;</span>
                        <span className="sr-only">Previous</span>
                    </a>
                </li>
                {windowStart > 0 ? (
                    <li className="page-item disabled">
                        <span className="page-link">...</span>
                    </li>
                ) : null}
                {Array.from(
                    { length: windowEnd - windowStart + 1 },
                    (_, i) => windowStart + i + 1
                ).map((i) => {
                    return (
                        <li className={"page-item" + (currentPage === i ? " active" : "")} key={i}>
                            <a
                                className="page-link"
                                href={getPageUrl?.(i)}
                                onClick={(e) => {
                                    handlers.triggerGoto(e, i)
                                }}
                            >
                                {i}
                            </a>
                        </li>
                    )
                })}
                {windowEnd < pagesCount - 1 ? (
                    <li className="page-item disabled">
                        <span className="page-link">...</span>
                    </li>
                ) : null}
                <li className={"page-item" + (currentPage >= pagesCount ? " disabled" : "")}>
                    <a className="page-link" href={nextPageUrl} onClick={handlers.triggerNext}>
                        <span aria-hidden="true">&raquo;</span>
                        <span className="sr-only">Next</span>
                    </a>
                </li>
            </ul>
        </div>
    )
}
